import { useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import {
    ExternalLink,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from "lucide-react";
import PaViewLink from "../ui/PaViewLink";

const CommissionsTable = ({ commissions, title }) => {
    const columns = useMemo(() => [
        {
            Header: 'Company',
            accessor: 'name',
            Cell: ({ row }) => (
                row.original.isPlanAdmin ? (
                    <PaViewLink
                        policyId={row.original.policy_Id}
                        className="text-primary hover:text-primary-dark font-medium inline-flex items-center gap-1 transition-colors"
                    >
                        {row.original.name}
                        <ExternalLink className="w-3 h-3" />
                    </PaViewLink>
                ) : (
                    <span className="font-medium text-gray-900 inline-flex items-center gap-2">
                        {row.original.name}
                        <span className="text-sm text-gray-400 font-normal">No Plan Admin Access</span>
                    </span>
                )
            ),
        },
        {
            Header: 'Amount',
            accessor: 'amount',
            Cell: ({ value }) => (
                <span className="font-medium text-gray-900">
                    ${parseFloat(value).toFixed(2)}
                </span>
            ),
            sortType: (rowA, rowB) => {
                return parseFloat(rowA.original.amount) - parseFloat(rowB.original.amount);
            },
        },
    ], []);

    const data = useMemo(() => commissions, [commissions]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 10 },
        },
        useSortBy,
        usePagination
    );

    return (
        <div className="overflow-x-auto">
            {title && (
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    {title}
                </h3>
            )}
            <table {...getTableProps()} className="w-full text-sm">
                <thead>
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()} className="border-b border-gray-100">
                            {headerGroup.headers.map(column => (
                                <th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    className={`py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition-colors ${
                                        column.id === 'amount' ? 'text-right' : 'text-left'
                                    }`}
                                >
                                    <span className={`inline-flex items-center gap-1 ${
                                        column.id === 'amount' ? 'justify-end' : ''
                                    }`}>
                                        {column.render('Header')}
                                        {column.isSorted && (
                                            column.isSortedDesc
                                                ? <ChevronDown className="w-3 h-3" />
                                                : <ChevronUp className="w-3 h-3" />
                                        )}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()} className="divide-y divide-gray-50">
                    {page.map(row => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                                {row.cells.map(cell => (
                                    <td
                                        {...cell.getCellProps()}
                                        className={`py-3 px-2 ${
                                            cell.column.id === 'amount' ? 'text-right' : ''
                                        }`}
                                    >
                                        {cell.render('Cell')}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* pagination - matching quotes page style */}
            {data.length > 10 && (
                <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="First page"
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={previousPage}
                            disabled={!canPreviousPage}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Previous page"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={nextPage}
                            disabled={!canNextPage}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Next page"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => gotoPage(pageOptions.length - 1)}
                            disabled={!canNextPage}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Last page"
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>
                            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                        </span>
                        <span className="flex items-center gap-2">
                            Go to:
                            <input
                                type="number"
                                min={1}
                                max={pageOptions.length}
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(pageNumber);
                                }}
                                className="w-14 px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                            />
                        </span>
                    </div>

                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                    >
                        {[10, 20, 30, 40].map((size) => (
                            <option key={size} value={size}>
                                Show {size}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
};

export default CommissionsTable;
